import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === 'zone_manager') {
        navigate('/gerente');
      } else if (user.role === 'recepcionista') {
        navigate('/pos');
      } else if (['master_admin', 'admin', 'manager'].includes(user.role)) {
        navigate('/admin');
      } else if (user.role === 'barber') {
        navigate('/barber');
      } else {
        navigate('/pos');
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    // Initialize admin on first load
    const initializeData = async () => {
      try {
        await axios.post(`${API}/auth/init-admin`);
      } catch (error) {
        // Already initialized, ignore
      }
    };
    initializeData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const userData = await login(username, password);
      toast.success(`Bienvenido, ${userData.name}`);
      
      // Redirect based on role
      if (userData.role === 'zone_manager') {
        window.location.href = '/gerente';
      } else if (userData.role === 'recepcionista') {
        window.location.href = '/pos';
      } else if (['master_admin', 'admin', 'manager'].includes(userData.role)) {
        window.location.href = '/admin';
      } else if (userData.role === 'barber') {
        window.location.href = '/barber';
      } else {
        window.location.href = '/pos';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1590360316066-357b23e0dd15?auto=format&fit=crop&q=80')` 
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-heading text-4xl text-white mb-4">
            Sistema de Punto de Venta
          </h2>
          <p className="text-zinc-400 text-lg">
            Gestiona tu barbería de forma profesional
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#050505] p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_8f620d75-9fbb-40ab-bbdd-47d8eeebdc0d/artifacts/z1jrqp78_IMG_0475.jpeg" 
              alt="The Holland Barbershop"
              className="w-16 h-16 object-contain"
            />
            <div>
              <h1 className="font-heading text-2xl text-white tracking-tight">
                The Holland
              </h1>
              <p className="text-xs text-zinc-500 uppercase tracking-widest">
                Barber Shop
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-400 uppercase text-xs tracking-widest">
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="h-12 bg-zinc-900 border-zinc-800 text-white focus:border-gold focus:ring-gold rounded-sm"
                data-testid="login-username-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 uppercase text-xs tracking-widest">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 bg-zinc-900 border-zinc-800 text-white focus:border-gold focus:ring-gold rounded-sm pr-12"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-gold text-black hover:bg-gold-dark font-semibold uppercase tracking-wide rounded-sm"
              data-testid="login-submit-btn"
            >
              {isLoading ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
